import { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import styled from 'styled-components'

import { clipboardCopy } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useCheckPrevPathname } from '../../hooks/useCheckPrevPathname'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { RowBetween } from '../../components/common'
import { BankIcon } from '../../components/bankIcon'
import { Button } from '../../components/button'
import { StyledText, Text } from '../../components/text'
import { Footer, FOOTER_MIN_HEIGHT } from '../../components/footer'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'

import { Color } from '../../theme/color'

export const SuccessDeposit = observer(() => {
  const {
    commonStore: { setShowTab, setInvalidAccess },
    myStore: { unmaskDepositAccountStore, withdrawalAccountStore },
  } = useContext(StoresContext)

  const [isLoading, setIsLoading] = useState<boolean>(true)

  const isValidPrevPathname = useCheckPrevPathname('/account/deposit/terms')

  const redirectHref = sessionStorage.getItem('createAccountSuccessCallbackLink')

  useEffect(() => {
    sessionStorage.setItem('isLastAccountCreatePage', 'true')
    setShowTab(false)
    getAccountData()
    return () => {
      sessionStorage.removeItem('createAccountSuccessCallbackLink')
    }
  }, [])

  useEffect(() => {
    if (isValidPrevPathname) {
      setInvalidAccess(true)
    }
  }, [isValidPrevPathname])

  const getAccountData = async () => {
    await unmaskDepositAccountStore.load()
    await withdrawalAccountStore.load()
    setIsLoading(false)
  }

  if (isLoading) {
    return <></>
  }

  const copyAccountNumber = async () => {
    if (!unmaskDepositAccountStore.content) return
    const { account } = unmaskDepositAccountStore.content
    clipboardCopy(account, { message: '예치금 계좌 번호 복사가 완료되었어요.', isFooter: false })
  }

  return (
    <Wrap>
      <StyledContent backgroundColor="gray_1">
        <AccountInfoContainer>
          <Text size={24} weight={600} lineHeight={34}>
            예치금 충전 계좌 발급이
            <br />
            완료되었어요.
          </Text>
          <SubTitle size={18} weight={600} block={true}>
            예치금 충전 계좌
          </SubTitle>
          <BankContainer>
            <RowBetween>
              <BankInfo>
                <BankIcon size={46} name="NH농협은행" />
                <Text size={18} weight={600}>
                  NH농협은행
                </Text>
              </BankInfo>
              <Button type="primary" size="compact" onClick={copyAccountNumber}>
                계좌번호 복사
              </Button>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                계좌번호
              </Text>
              <Text size={14} color="text_secondary">
                {unmaskDepositAccountStore?.content?.account}
              </Text>
            </RowBetween>
            <RowBetween>
              <Text size={14} weight={600} color="text_secondary">
                예금주
              </Text>
              <Text size={14} color="text_secondary">
                {unmaskDepositAccountStore?.content?.bankOwner}
              </Text>
            </RowBetween>
          </BankContainer>
        </AccountInfoContainer>
        <NoticeContainer
          title="안내사항"
          list={[
            '예치금 충전은 등록하신 출금 계좌로만 가능해요.',
            '은행 전산 시스템 상태에 따라 반영되는 시간이 소요될 수 있어요.',
            '은행 점검 시간(23:40 ~ 00:20)에는 충전 및 출금 신청이 제한될 수 있어요.',
            '본 계좌 번호가 아닌 평생 계좌 번호(휴대폰, 사업자 번호 등)는 등록이 불가능해요.',
            '같은 계좌이지만, 계좌 번호가 변경된 경우, 변경된 계좌 번호를 입력해 주세요.',
            <Text size={14} lineHeight={22} color="error">
              금융사의 정책으로 오픈 뱅킹, 간편 결제를 통한 입금은 제한될 수도 있어요.
            </Text>,
          ]}
          $backgroundColor="gray_1"
        />
      </StyledContent>
      <Footer>
        <Button
          type="primary"
          size="large"
          block={true}
          href={redirectHref || '/myPage'}
          routeProps={{ replace: true }}
        >
          확인
        </Button>
      </Footer>
    </Wrap>
  )
})

const StyledContent = styled(Content)`
  padding-bottom: ${FOOTER_MIN_HEIGHT}px;
`
const AccountInfoContainer = styled.div`
  padding: 56px 24px 16px;
  background-color: ${Color.white};
`

const SubTitle = styled(StyledText)`
  margin: 32px 0 16px;
`

const BankContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  padding-bottom: 24px;
  > ${RowBetween} {
    margin-bottom: 8px;
  }
`

const BankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`
